import { useCallback, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { ArrowDown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { deviceIconMap } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";

import { DEVICE_LABEL_KEYS } from "../constants";

import { DropdownTrigger } from "./styles";

type Props = {
    selectedDevice: LocationDevice;
    deviceOptions: LocationDevice[];
    onDeviceSelect: (device: LocationDevice) => void;
};

export const DevicePicker = ({ selectedDevice, deviceOptions, onDeviceSelect }: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = useCallback(
        (device: LocationDevice) => {
            onDeviceSelect(device);
            setIsOpen(false);
        },
        [onDeviceSelect],
    );

    const DeviceIcon = deviceIconMap[selectedDevice];

    return (
        <Combobox
            isOpen={isOpen}
            data-automation="add-keywords-modal-device-picker"
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>
                <DropdownTrigger data-automation="add-keywords-modal-device-picker-trigger">
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <DeviceIcon
                            size="xs"
                            color={colorsPalettes.carbon[200]}
                        />
                        <Text fontVariant="value">{translate(DEVICE_LABEL_KEYS[selectedDevice])}</Text>
                    </Flex>
                    <ArrowDown size="xs" />
                </DropdownTrigger>
            </Combobox.Trigger>

            <Combobox.Content data-automation="add-keywords-modal-device-picker-content">
                {deviceOptions.map((device) => {
                    const Icon = deviceIconMap[device];
                    return (
                        <Combobox.Option
                            key={device}
                            data-automation={`add-keywords-modal-device-picker-option-${device.toLocaleLowerCase()}`}
                            isSelected={device === selectedDevice}
                            onClick={() => handleSelect(device)}
                        >
                            <Flex
                                alignItems="center"
                                gap="8px"
                            >
                                <Icon
                                    size="xs"
                                    color={colorsPalettes.carbon[200]}
                                />
                                <Combobox.OptionLabel>{translate(DEVICE_LABEL_KEYS[device])}</Combobox.OptionLabel>
                            </Flex>
                        </Combobox.Option>
                    );
                })}
            </Combobox.Content>
        </Combobox>
    );
};
